// src/pages/requisiciones/RequisicionFormPage.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Table, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import moment from 'moment';
import { 
  crearRequisicion, 
  cambiarEstadoRequisicion, 
  obtenerRequisicion,
  actualizarRequisicion 
} from '../../services/requisiciones/requisicionService';
import { getActividadesOpen, getDataList } from '../../api/common';
import { useAuth } from '../../hooks/useAuth';
import { Alertassw } from '../../components/Alertas/Alertassw';
import Swal from 'sweetalert2';

const RequisicionFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userData } = useAuth();
  const esEdicion = !!id;

  const [loading, setLoading] = useState(esEdicion);
  const [guardando, setGuardando] = useState(false);
  
  // Formulario (TIN-23: agregados tipoRequisicion, tipoServicio, observacionGeneral)
  const [formData, setFormData] = useState({
    titulo: '',
    tipoRequisicion: 'Patron',
    tipoServicio: '',
    observacionGeneral: '',
    observacionComercial: '',
    areaOrigen: userData?.area || '',
    items: []
  });

  // TIN-23 iter 2: estado real de la requisición (solo en modo edición)
  // para mostrar los botones apropiados sin forzar transiciones regresivas.
  const [estadoRequisicion, setEstadoRequisicion] = useState(null);

  console.log('formData :', formData);

  // Para buscar actividades
  const [actividades, setActividades] = useState([]);

  console.log('Actividades cargadas:', actividades);
  const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
  const [loadingActividades, setLoadingActividades] = useState(false);

  useEffect(() => {
    cargarActividades();
    if (esEdicion) {
      cargarRequisicion();
    }
  }, []);

  const cargarActividades = async () => {
    setLoadingActividades(true);
    try {
      const response = await getActividadesOpen('Actividad', {
        conditions: { estado: 'Activo' }
      });
      
      if (response.status === 200 && response.data?.data) {

        console.log('Actividades obtenidas del API:', response.data.data);
        const actividadesFormateadas = response.data.data.map(act => ({
          value: act._id,
          label: `${act.tipoActividad || ''} - ${act.equipo?.item || ''} ${act.equipo?.marca || ''} ${act.equipo?.modelo || ''}`,
          data: act
        })).sort((a, b) => a.label.localeCompare(b.label));
        
        setActividades(actividadesFormateadas);
      }
    } catch (error) {
      console.error('Error al cargar actividades:', error);
      Alertassw('error', 'Error', 'Error al cargar actividades');
    } finally {
      setLoadingActividades(false);
    }
  };

  const cargarRequisicion = async () => {
    setLoading(true);
    try {
      const response = await obtenerRequisicion(id);
      
      if (response.status === 200 && response.data?.data) {
        const req = response.data.data;
        
        // Mapear items conservando snapshotPatron.requerimientos editado (TIN-23 CA-13)
        const itemsFormateados = (req.items || []).map(item => ({
          actividad: item.actividad?._id || item.actividad,
          patron: item.patron?._id || item.patron,
          tipoActividad: item.tipoActividad || '',
          equipoInfo: {
            item: item.actividad?.equipo?.item || item.equipoInfo?.item || '',
            marca: item.actividad?.equipo?.marca || item.equipoInfo?.marca || '',
            modelo: item.actividad?.equipo?.modelo || item.equipoInfo?.modelo || '',
            serie: item.actividad?.equipo?.serie || item.equipoInfo?.serie || ''
          },
          patronInfo: {
            item: item.patron?.item || item.patronInfo?.item || '',
            marca: item.patron?.marca || item.patronInfo?.marca || '',
            modelo: item.patron?.modelo || item.patronInfo?.modelo || ''
          },
          // El campo editable: viene del snapshot persistido en el backend
          requerimientosEditados:
            item.snapshotPatron?.requerimientos ??
            item.requerimientosCalibracion ??
            item.patron?.requerimientosCalibracion ??
            '',
          descripcionServicio: item.descripcionServicio || '',
          observacion: item.observacion || '' // legacy preservado
        }));

        setFormData({
          titulo: req.titulo,
          tipoRequisicion: req.tipoRequisicion || 'Patron',
          tipoServicio: req.tipoServicio || '',
          observacionGeneral: req.observacionGeneral || '',
          observacionComercial: req.observacionComercial || '',
          areaOrigen: req.areaOrigen,
          items: itemsFormateados
        });
        setEstadoRequisicion(req.estado || null);
      } else {
        Alertassw('error', 'Error', 'No se pudo cargar la requisición');
        navigate('/requisiciones');
      }
    } catch (error) {
      console.error('Error al cargar requisición:', error);
      Alertassw('error', 'Error', 'Error al cargar la requisición');
      navigate('/requisiciones');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // TIN-23: al cambiar tipoRequisicion, los items previos quedan inválidos
  // (uno tiene actividad+patron, el otro descripcionServicio). Si hay items,
  // pedimos confirmación y los limpiamos para evitar payload mixto.
  const handleCambiarTipoRequisicion = async (e) => {
    const nuevoTipo = e.target.value;
    if (nuevoTipo === formData.tipoRequisicion) return;

    if (formData.items.length === 0) {
      setFormData({
        ...formData,
        tipoRequisicion: nuevoTipo,
        tipoServicio: nuevoTipo === 'Servicio_General' ? formData.tipoServicio : ''
      });
      return;
    }

    const confirma = await Swal.fire({
      title: '¿Cambiar tipo de requisición?',
      html: `Tienes <strong>${formData.items.length}</strong> item(s) cargados que no son compatibles con el nuevo tipo y serán <strong>eliminados</strong>.<br/>¿Deseas continuar?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar tipo',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    });
    if (!confirma.isConfirmed) {
      // Forzar al select volver al valor anterior
      e.target.value = formData.tipoRequisicion;
      return;
    }

    setFormData({
      ...formData,
      tipoRequisicion: nuevoTipo,
      tipoServicio: nuevoTipo === 'Servicio_General' ? formData.tipoServicio : '',
      items: []
    });
    setActividadSeleccionada(null);
  };

  const handleAgregarActividad = () => {
    if (!actividadSeleccionada) {
      Alertassw('toast', 'Seleccione una actividad', '', 'warning');
      return;
    }

    const actividadData = actividadSeleccionada.data;

    console.log('Actividad seleccionada para agregar:', actividadData);
    
    // Verificar que no esté duplicada
    if (formData.items.some(item => item.actividad === actividadData._id)) {
      Alertassw('toast', 'Esta actividad ya está agregada', '', 'warning');
      return;
    }

    const nuevoItem = {
      actividad: actividadData._id,
      patron: actividadData.equipo?._id || actividadData.equipo?._id,
      tipoActividad: actividadData.tipoActividad,
      equipoInfo: {
        item: actividadData.equipo?.item || '',
        marca: actividadData.equipo?.marca || '',
        modelo: actividadData.equipo?.modelo || '',
        serie: actividadData.equipo?.serie || ''
      },
      patronInfo: {
        item: actividadData.equipo?.item || actividadData.equipo?.item || '',
        marca: actividadData.equipo?.marca || actividadData.equipo?.marca || '',
        modelo: actividadData.equipo?.modelo || actividadData.equipo?.modelo || ''
      },
      // TIN-23 CA-13: precarga de requerimientos editables
      requerimientosEditados: actividadData.equipo?.requerimientosCalibracion || '',
      descripcionServicio: '',
      observacion: '' // legacy
    };

    setFormData({
      ...formData,
      items: [...formData.items, nuevoItem]
    });

    setActividadSeleccionada(null);
    Alertassw('toast', 'Actividad agregada', '', 'success');
  };

  const handleEliminarItem = (index) => {
    if (window.confirm('¿Está seguro de eliminar este item?')) {
      const nuevosItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: nuevosItems });
    }
  };

  const handleObservacionItem = (index, valor) => {
    const nuevosItems = [...formData.items];
    nuevosItems[index].observacion = valor;
    setFormData({ ...formData, items: nuevosItems });
  };

  // TIN-23 CA-13: edición de requerimientos preservada en backend (snapshotPatron.requerimientos)
  const handleRequerimientosItem = (index, valor) => {
    const nuevosItems = [...formData.items];
    nuevosItems[index].requerimientosEditados = valor;
    setFormData({ ...formData, items: nuevosItems });
  };

  const handleDescripcionServicioItem = (index, valor) => {
    const nuevosItems = [...formData.items];
    nuevosItems[index].descripcionServicio = valor;
    setFormData({ ...formData, items: nuevosItems });
  };

  const handleAgregarItemServicioGeneral = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          actividad: undefined,
          patron: undefined,
          tipoActividad: '',
          equipoInfo: { item: 'Servicio general', marca: '', modelo: '', serie: '' },
          patronInfo: { item: '', marca: '', modelo: '' },
          requerimientosEditados: '',
          descripcionServicio: '',
          observacion: ''
        }
      ]
    });
  };

  const validarFormulario = () => {
    if (!formData.titulo.trim()) {
      Alertassw('toast', 'Ingrese el título de la requisición', '', 'warning');
      return false;
    }

    if (formData.tipoRequisicion === 'Servicio_General' && !formData.tipoServicio.trim()) {
      Alertassw('toast', 'Indique el tipo de servicio para requisiciones de servicio general', '', 'warning');
      return false;
    }

    if (formData.items.length === 0) {
      Alertassw('toast', 'Debe agregar al menos un item', '', 'warning');
      return false;
    }

    if (formData.tipoRequisicion === 'Servicio_General') {
      const itemSinDescripcion = formData.items.findIndex(it => !(it.descripcionServicio || '').trim());
      if (itemSinDescripcion >= 0) {
        Alertassw('toast', `Item ${itemSinDescripcion + 1}: falta la descripción del servicio`, '', 'warning');
        return false;
      }
    }

    return true;
  };

  // Construye el payload que el backend espera (TIN-23: incluye snapshotPatron.requerimientos
  // con el valor editado, para que el backend respete la edición manual y NO recalcule).
  const construirPayload = () => ({
    titulo: formData.titulo,
    tipoRequisicion: formData.tipoRequisicion,
    tipoServicio: formData.tipoServicio,
    observacionGeneral: formData.observacionGeneral,
    observacionComercial: formData.observacionComercial,
    areaOrigen: formData.areaOrigen,
    items: formData.items.map(item => ({
      actividad: item.actividad,
      patron: item.patron,
      tipoActividad: item.tipoActividad,
      // CA-13: snapshotPatron.requerimientos enviado explícitamente preserva la edición
      snapshotPatron: { requerimientos: item.requerimientosEditados || '' },
      descripcionServicio: item.descripcionServicio || ''
      // observacion legacy: ya no se envía (deprecated TIN-23)
    }))
  });

  const handleGuardarBorrador = async () => {
    if (!validarFormulario()) return;

    setGuardando(true);
    try {
      const data = construirPayload();

      let response;
      if (esEdicion) {
        response = await actualizarRequisicion(id, data);
      } else {
        response = await crearRequisicion(data);
      }

      if (response.status === 200 || response.status === 201) {
        Alertassw('success', 'Éxito', 'Requisición guardada correctamente');
        navigate('/requisiciones');
      } else {
        Alertassw('error', 'Error', response.data?.message || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      Alertassw('error', 'Error', 'Error al guardar la requisición');
    } finally {
      setGuardando(false);
    }
  };

  const handleEnviarComercial = async () => {
    if (!validarFormulario()) return;

    if (!window.confirm('¿Está seguro de enviar esta requisición a comercial?')) {
      return;
    }

    setGuardando(true);
    try {
      // Si es nueva, primero la creamos
      if (!esEdicion) {
        const data = construirPayload();
        const responseCrear = await crearRequisicion(data);
        
        if (responseCrear.status === 201 && responseCrear.data?.data?._id) {
          const nuevoId = responseCrear.data.data._id;
          
          // Cambiar estado a Enviada_Comercial
          const responseEstado = await cambiarEstadoRequisicion(nuevoId, {
            estado: 'Enviada_Comercial',
            observacion: 'Enviada desde formulario de creación'
          });

          if (responseEstado.status === 200) {
            Alertassw('success', 'Éxito', 'Requisición enviada a comercial');
            navigate('/requisiciones');
          } else {
            Alertassw('error', 'Error', 'Error al cambiar el estado');
          }
        } else {
          Alertassw('error', 'Error', 'Error al crear la requisición');
        }
      } else {
        // Si ya existe, solo cambiamos el estado
        const response = await cambiarEstadoRequisicion(id, {
          estado: 'Enviada_Comercial',
          observacion: 'Enviada desde formulario de edición'
        });

        if (response.status === 200) {
          Alertassw('success', 'Éxito', 'Requisición enviada a comercial');
          navigate('/requisiciones');
        } else {
          Alertassw('error', 'Error', response.data?.message || 'Error al cambiar el estado');
        }
      }
    } catch (error) {
      console.error('Error al enviar:', error);
      Alertassw('error', 'Error', 'Error al enviar la requisición');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <Card className='w-100'>
        <Card.Header className="bg-primary text-white w-100">
          <h5 className="mb-0">
            <i className="bi bi-clipboard-plus me-2"></i>
            {esEdicion ? 'Editar Requisición' : 'Nueva Requisición'}
          </h5>
        </Card.Header>
        
        <Card.Body className='w-100'>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  Título <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  placeholder="Ej: Calibración equipos laboratorio Q1 2026"
                />
              </Form.Group>
            </Col>

            {/* TIN-23: selector tipoRequisicion */}
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Tipo de requisición</Form.Label>
                <Form.Select
                  name="tipoRequisicion"
                  value={formData.tipoRequisicion}
                  onChange={handleCambiarTipoRequisicion}
                  disabled={esEdicion}
                >
                  <option value="Patron">Patrón / Actividad metrológica</option>
                  <option value="Servicio_General">Servicio general (sin patrón)</option>
                </Form.Select>
                {esEdicion ? (
                  <Form.Text className="text-muted small">
                    El tipo no se puede modificar una vez creada la requisición.
                  </Form.Text>
                ) : formData.items.length > 0 ? (
                  <Form.Text className="text-warning small">
                    ⚠ Cambiar el tipo eliminará los {formData.items.length} item(s) cargados.
                  </Form.Text>
                ) : null}
              </Form.Group>
            </Col>

            {/* TIN-23: tipoServicio condicional */}
            {formData.tipoRequisicion === 'Servicio_General' && (
              <Col xs={12} md={8}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Tipo de servicio <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="tipoServicio"
                    value={formData.tipoServicio}
                    onChange={handleChange}
                    placeholder="Ej: Mantenimiento de infraestructura, calibración externa, insumos..."
                  />
                </Form.Group>
              </Col>
            )}

            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-bold">Observación general</Form.Label>
                <Form.Control
                  as="textarea"
                  name="observacionGeneral"
                  value={formData.observacionGeneral}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Observaciones a nivel de requisición — visibles también para los proveedores."
                />
                <Form.Text className="text-muted small">
                  TIN-23: campo único a nivel de requisición. Reemplaza la observación por ítem.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-bold">Observación Comercial (interna)</Form.Label>
                <Form.Control
                  as="textarea"
                  name="observacionComercial"
                  value={formData.observacionComercial}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Observaciones internas para el área comercial..."
                />
              </Form.Group>
            </Col>

            {/* SECCIÓN DE ITEMS */}
            <Col xs={12}>
              <Card className="border w-100">
                <Card.Header className="bg-light w-100">
                  <h6 className="mb-0">
                    <i className="bi bi-list-check me-2"></i>
                    Items de la Requisición
                  </h6>
                </Card.Header>
                <Card.Body className='w-100'>
                  {/* TIN-23: buscador de actividades solo para tipoRequisicion = Patron */}
                  {formData.tipoRequisicion === 'Patron' ? (
                    <Row className="g-2 mb-3">
                      <Col xs={12} md={9}>
                        <Form.Label className="small fw-bold">Buscar Actividad</Form.Label>
                        <Select
                          options={actividades}
                          value={actividadSeleccionada}
                          onChange={setActividadSeleccionada}
                          placeholder="Buscar por equipo o tipo de actividad..."
                          isSearchable
                          isClearable
                          isLoading={loadingActividades}
                          noOptionsMessage={() => "No se encontraron actividades"}
                          styles={{
                            control: (base) => ({ ...base, minHeight: '38px' })
                          }}
                        />
                      </Col>
                      <Col xs={12} md={3} className="d-flex align-items-end">
                        <Button
                          variant="primary"
                          className="w-100"
                          onClick={handleAgregarActividad}
                          disabled={!actividadSeleccionada}
                        >
                          <i className="bi bi-plus-circle me-1"></i>
                          Agregar
                        </Button>
                      </Col>
                    </Row>
                  ) : (
                    <Row className="g-2 mb-3">
                      <Col xs={12}>
                        <Button variant="primary" onClick={handleAgregarItemServicioGeneral}>
                          <i className="bi bi-plus-circle me-1"></i>
                          Agregar item de servicio
                        </Button>
                      </Col>
                    </Row>
                  )}

                  {/* Tabla de items agregados */}
                  {formData.items.length === 0 ? (
                    <Alert variant="info" className="text-center">
                      <i className="bi bi-info-circle me-2"></i>
                      No hay items agregados. {formData.tipoRequisicion === 'Patron'
                        ? 'Busque y agregue actividades.'
                        : 'Agregue items de servicio.'}
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table bordered hover size="sm">
                        <thead className="table-light">
                          <tr>
                            {formData.tipoRequisicion === 'Patron' && (
                              <>
                                <th style={{ width: '120px' }}>Tipo Actividad</th>
                                <th>Equipo</th>
                                <th>Patrón</th>
                                <th>Requerimientos (editable)</th>
                              </>
                            )}
                            {formData.tipoRequisicion === 'Servicio_General' && (
                              <th>Descripción del servicio</th>
                            )}
                            <th style={{ width: '60px' }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index}>
                              {formData.tipoRequisicion === 'Patron' && (
                                <>
                                  <td>{item.tipoActividad}</td>
                                  <td>
                                    <small>
                                      <strong>{item.equipoInfo?.item || 'N/A'}</strong><br />
                                      {item.equipoInfo?.marca || ''} {item.equipoInfo?.modelo || ''}<br />
                                      S/N: {item.equipoInfo?.serie || 'N/A'}
                                    </small>
                                  </td>
                                  <td>
                                    <small>
                                      {item.patronInfo?.item || 'N/A'}<br />
                                      {item.patronInfo?.marca || ''} {item.patronInfo?.modelo || ''}
                                    </small>
                                  </td>
                                  <td>
                                    {/* TIN-23 CA-13: editable, el backend preserva el valor enviado */}
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      size="sm"
                                      value={item.requerimientosEditados || ''}
                                      onChange={(e) => handleRequerimientosItem(index, e.target.value)}
                                      placeholder="Requerimientos para esta actividad..."
                                    />
                                  </td>
                                </>
                              )}
                              {formData.tipoRequisicion === 'Servicio_General' && (
                                <td>
                                  <Form.Control
                                    as="textarea"
                                    rows={3}
                                    size="sm"
                                    value={item.descripcionServicio || ''}
                                    onChange={(e) => handleDescripcionServicioItem(index, e.target.value)}
                                    placeholder="Describe el servicio requerido..."
                                  />
                                </td>
                              )}
                              <td className="text-center">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleEliminarItem(index)}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* BOTONES — TIN-23 iter 2: copy explícito sobre el flujo de re-aprobación */}
            <Col xs={12}>
              {(() => {
                // El backend ahora regresa AUTOMÁTICAMENTE la requisición a
                // "Enviada para Aprobación" cuando se edita una que ya pasó
                // de Borrador (regla del flujo: editar implica re-aprobar).
                // El frontend solo refleja esto en el copy del botón.
                const yaAvanzada = esEdicion && estadoRequisicion && estadoRequisicion !== 'Borrador';

                return (
                  <div className="d-flex justify-content-end gap-2 flex-wrap align-items-center">
                    {yaAvanzada && (
                      <small className="text-warning me-2">
                        <i className="bi bi-info-circle me-1"></i>
                        Al guardar, la requisición volverá a <strong>Enviada para Aprobación</strong>.
                      </small>
                    )}
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/requisiciones')}
                      disabled={guardando}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Cancelar
                    </Button>
                    <Button
                      variant={yaAvanzada ? 'warning' : 'secondary'}
                      onClick={handleGuardarBorrador}
                      disabled={guardando}
                    >
                      {guardando ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Guardando...
                        </>
                      ) : yaAvanzada ? (
                        <>
                          <i className="bi bi-arrow-counterclockwise me-1"></i>
                          Guardar y re-enviar para Aprobación
                        </>
                      ) : (
                        <>
                          <i className="bi bi-save me-1"></i>
                          Guardar Borrador
                        </>
                      )}
                    </Button>
                    {!yaAvanzada && (
                      <Button
                        variant="primary"
                        onClick={handleEnviarComercial}
                        disabled={guardando}
                      >
                        {guardando ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send me-1"></i>
                            Enviar para Aprobación
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                );
              })()}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default RequisicionFormPage;
